import { Injectable, inject } from '@angular/core';
import { DocumentHandler, DesignDocument, WmsDataService, MigrationService, bounds_checkout_filter, design_filter } from '@luzula/luzula-lib';
import { AufnahmePunktDocument } from '../data-model';
import { updateBohrpunkt } from '../data-update.functions';

// JSON-Imports für HardData
import Bestandsbeschreibungen from '../../assets/json/Bestandsbeschreibungen.json';
import Bodenarten from '../../assets/json/Bodenarten.json';
import BodenartenTabelle from '../../assets/json/BodenartenTabelle.json';
import BodenformSuffixe from '../../assets/json/BodenformSuffixe.json';
import Bodenvegetationen from '../../assets/json/Bodenvegetationen.json';
import Farben from '../../assets/json/Farben.json';
import Feinbodenformen from '../../assets/json/Feinbodenformen.json';
import HorizontSymbole from '../../assets/json/HorizontSymbole.json';
import Humusformen from '../../assets/json/Humusformen.json';
import Punktarten from '../../assets/json/Punktarten.json';
import Nährkraftstufen from '../../assets/json/Nährkraftstufen.json';
import ReliefformUmgebung from '../../assets/json/ReliefformUmgebung.json';
import Reviere from '../../assets/json/Reviere.json';
import Substrattypengruppen from '../../assets/json/Substrattypengruppen.json';
import Torfarten from '../../assets/json/Torfarten.json';
import Wuchsgebiete from '../../assets/json/Wuchsgebiete.json';
import Wuchsbezirke from '../../assets/json/Wuchsbezirke.json';
import Forstämter from '../../assets/json/Forstämter.json';
import Neigungsstufe from '../../assets/json/Neigungsstufe.json';
import Woelbung from '../../assets/json/Woelbung.json';
import Hangrichtung from '../../assets/json/Hangrichtung.json';
import Hanganschluss from '../../assets/json/Hanganschluss.json';
import Hangteil from '../../assets/json/Hangteil.json';
import InnereRauhheit from '../../assets/json/InnereRauhheit.json';
import Klimastufen from '../../assets/json/Klimastufen.json';
import { LocationSystem, ValidationLevel } from '../data-model';

/**
 * Implementierung des DocumentHandler-Interfaces für AufnahmePunktDocument.
 * Kapselt alle Bohrpunkte-spezifische Logik für Transformation,
 * Validation, Enrichment und Filterung.
 */
@Injectable({
	providedIn: 'root'
})
export class BohrpunktDocumentHandler implements DocumentHandler<AufnahmePunktDocument> {
	private wmsData = inject(WmsDataService);
	private migrationService = inject(MigrationService);

	/**
	 * Liefert CouchDB Design-Dokumente (Views und Filter) für Bohrpunkte.
	 */
	getDesignDocuments(): DesignDocument[] {
		const bounds_checkout_filter_str = bounds_checkout_filter.toString();
		const design_filter_str = design_filter.toString();

		const view_map_str = 'function(doc) { \
			if (doc._id === "_design/filter") emit(doc._id); \
			if (doc.header && doc.type && doc.header.koordinaten.x && doc.header.koordinaten.y) { \
			  var h = doc.header; \
			  emit([ h.nummer, doc.type, h.koordinaten.x, h.koordinaten.y, { \
			    nummer: h.nummer, koordinaten: h.koordinaten, \
			    forstamt: h.forstamt, forstbetrieb: h.forstbetrieb, revier: h.revier, \
			    adresse: h.adresse, aufnahme: h.aufnahme, bodenform: h.bodenform, \
			    humusform: h.humusform, vegetationsformengruppe: h.vegetationsformengruppe, \
			    substratFeuchte: h.substratFeuchte, bemerkungen: h.bemerkungen, \
			    wasserstufe: h.wasserstufe, wasserstufe2: h.wasserstufe2, \
			    projects: h.projects \
			  }, doc.state, doc.version, doc.extendedHeader ? doc.extendedHeader.mesoklima : null, doc.extendedHeader && doc.extendedHeader.stfg ? { sf1: (doc.extendedHeader.stfg.stammFeuchteStufe || {}).first || "", nk: doc.extendedHeader.stfg.naehrkraftStufe || "", sf: (doc.extendedHeader.stfg.stammFeuchteStufe || {}).second || "" } : null ]); \
			} \
		}'.replaceAll('\t', '');

		return [{
			_id: '_design/application',
			views: {
				index: {
					map: view_map_str
				}
			},
			filters: {
				bounds: bounds_checkout_filter_str,
				design: design_filter_str
			}
		}];
	}

	/**
	 * Transformiert ein Rohes Dokument nach dem Laden aus der Datenbank.
	 * Führt Migrationen und Updates durch.
	 */
	transformAfterLoad(raw: any): AufnahmePunktDocument {
		// Versionsmigration über MigrationService
		let doc = this.migrationService.migrate(raw);

		// Transformationen für aktuelle Version (non-version-changing)
		doc = updateBohrpunkt(doc);

		return doc as AufnahmePunktDocument;
	}

	/**
	 * Enrichment: Reichert ein Dokument mit WMS-Geodaten an (Substrat, Klima, etc.).
	 * Modifiziert das Dokument in-place via WMS-Abfrage.
	 */
	enrichDocument(doc: AufnahmePunktDocument): void {
		// WMS-Daten asynchron laden (in-place modification)
		this.wmsData.fetchWmsDataForDocument(doc);
	}

	/**
	 * Prüft ob ein Bohrpunkt in einer Region liegt (für räumliche Filterung beim Download).
	 * Region-Format: {l: {x, y}, u: {x, y}, polygon?: Array<{x, y}>}
	 */
	isDocumentInRegion(doc: AufnahmePunktDocument, region: any): boolean {
		if (!doc.header?.koordinaten?.x || !doc.header?.koordinaten?.y) {
			return false;
		}

		const x = doc.header.koordinaten.x;
		const y = doc.header.koordinaten.y;

		// Schneller Bounding-Box-Check
		if (x < region.l.x || x > region.u.x || y < region.l.y || y > region.u.y) {
			return false;
		}

		// Wenn Polygon definiert, genauerer Check mit Point-in-Polygon
		if (region.polygon && Array.isArray(region.polygon) && region.polygon.length >= 3) {
			return this.isPointInPolygon(x, y, region.polygon);
		}

		// Nur Bounding-Box, kein Polygon → Punkt liegt in Region
		return true;
	}

	/**
	 * Point-in-Polygon Test (Ray-Casting-Algorithmus).
	 */
	private isPointInPolygon(px: number, py: number, polygon: Array<{ x: number, y: number }>): boolean {
		let inside = false;
		for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
			const xi = polygon[i].x, yi = polygon[i].y;
			const xj = polygon[j].x, yj = polygon[j].y;
			if (((yi > py) !== (yj > py)) && (px < (xj - xi) * (py - yi) / (yj - yi) + xi)) {
				inside = !inside;
			}
		}
		return inside;
	}

	/**
	 * Transformation vor dem Speichern in die Datenbank.
	 * Aktuell: Keine spezielle Transformation nötig.
	 */
	transformForSave(doc: AufnahmePunktDocument): AufnahmePunktDocument {
		return doc;
	}

	/**
	 * Liefert hardcodierte Lookup-Daten (Bodenarten, Farben, etc.).
	 * Wird von BaseDataService gecacht.
	 */
	getHardData(): Map<string, any[]> {
		const hardData = new Map<string, any[]>();

		hardData.set('Punktarten', Punktarten);
		hardData.set('Bestandsbeschreibungen', Bestandsbeschreibungen);
		hardData.set('Bodenarten', Bodenarten);
		// BodenartenTabelle ist ein Object, kein Array - wrappen falls als Array benötigt
		hardData.set('BodenartenTabelle', Array.isArray(BodenartenTabelle) ? BodenartenTabelle : [BodenartenTabelle]);
		hardData.set('BodenformSuffixe', BodenformSuffixe);
		hardData.set('Bodenvegetationen', Bodenvegetationen);
		hardData.set('Farben', Farben);
		hardData.set('Feinbodenformen', Feinbodenformen);
		hardData.set('HorizontSymbole', HorizontSymbole);
		hardData.set('Humusformen', Humusformen);
		hardData.set('ReliefformUmgebung', ReliefformUmgebung);
		hardData.set('Reviere', Reviere);
		hardData.set('Nährkraftstufen', Nährkraftstufen);
		hardData.set('Substrattypengruppen', Substrattypengruppen);
		hardData.set('Torfarten', Torfarten);
		hardData.set('Qualitätsstufen', [
			{ id: 1, name: 'Altformular / Umstufung' },
			{ id: 2, name: 'unvollständige Kartierung' },
			{ id: 3, name: 'Aktuelle Kartierung' }
		]);
		hardData.set('Koordinatensysteme', Object.values(LocationSystem));
		hardData.set('Wuchsgebiete', Wuchsgebiete);
		hardData.set('Wuchsbezirke', Wuchsbezirke);
		hardData.set('Forstämter', Forstämter);
		hardData.set('Neigungsstufe', Neigungsstufe);
		hardData.set('Woelbung', Woelbung);
		hardData.set('Hangrichtung', Hangrichtung);
		hardData.set('Hanganschluss', Hanganschluss);
		hardData.set('Hangteil', Hangteil);
		hardData.set('InnereRauhheit', InnereRauhheit);
		hardData.set('Klimastufen', Klimastufen);

		return hardData;
	}

	/**
	 * Factory: Erstellt einen neuen Bohrpunkt mit Default-Werten.
	 * WMS-Enrichment muss separat via enrichDocument() aufgerufen werden.
	 */
	createNew(partial?: Partial<AufnahmePunktDocument>): AufnahmePunktDocument {
		// Basis-Struktur eines neuen Bohrpunkts (analog zu punktTemplate)
		const header = {
			nummer: '',
			forstamt: '',
			forstbetrieb: '',
			revier: '',
			adresse: {
				gemarkung: '',
				abteilung: '',
				unterabteilung: '',
				teilflaeche: ''
			},
			koordinaten: {
				system: LocationSystem.EPSG5650,
				x: 0,
				y: 0
			},
			aufnahme: {
				datum: (new Date(Date.now())).toISOString(),
				kartierer: ''
			},
			bodenform: {
				bf: [{
					trenner: '' as const,
					hbf: '',
					fbf: '',
				}],
				prefix: '',
				suffix: ''
			},
			humusform: {
				aufnahmeFlaeche: { neu: '' }
			},
			vegetationsformengruppe: '',
			substratFeuchte: '',
			bemerkungen: '',
			projects: [],
			...partial?.header
		};

		const extendedHeader = {
			geologie: {
				oben: '',
				trenner: undefined,
				unten: undefined,
			},
			morphologie: [ '', '' ],
			relief: {
				relief: ''
			},
			humusAuflage: {},
			klimaStufe: { neu: '' as const },
			stfg: {
				stammFeuchteStufe: {
					first: '',
					second: ''
				},
				naehrkraftStufe: '',
				substratGruppe: ''
			},
			baumarten: '',
			entwaesserungsverhalten: '' as const,
			kMgCaPSerie: '',
			nWsk: 0,
			nWskStufe: '',
			mesoklima: '' as const,
			gHorizont: {
				go: '',
				gr: '',
				g: '',
			},
			sHorizont: {
				sw: '',
				sd: '',
				s: '',
			},
			ph: {
				wert1: undefined,
				ort1: '' as const,
				wert2: undefined,
				ort2: '' as const,
				durchschnitt: undefined
			},
			...partial?.extendedHeader
		};

		const newDoc: any = {
			type: partial?.type || '6_Tiefbohrung',
			version: 6,
			quality: 1,
			state: {
				dataSeverity: ValidationLevel.ok,
				commentsSeverity: ValidationLevel.ok,
				hasDiskussions: false
			},
			checkerComments: new Map(),
			header,
			extendedHeader,
			Substratfolge: partial?.Substratfolge || [],
			Horizontfolge: partial?.Horizontfolge || [],
			bodenvegetation: partial?.bodenvegetation || {
				aufnahme: {
					schichtAnteile: {},
					vegetationen: []
				}
			},
		};

		return newDoc as AufnahmePunktDocument;
	}
}
